"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@repo/design-system/utils/cn";
import { displayDate, isBeforeToday } from "@repo/design-system/utils/date-helpers";
import { combineEmploymentTypes } from "@repo/design-system/utils/helpers";
import { formatSalaryRow, getSalaryState } from "@repo/design-system/utils/salary-display";
import { Printer } from "lucide-react";
import { useEffect, useState } from "react";
import PrivacyNotice from "~/components/job-page/jobadtemplate/privacy-notice-eon";
import JobAdVideo from "~/components/job-page/jobadvideo/jobad-video-eon";
import { Button } from "~/components/ui/button-eon";
import { LocationDisplay } from "~/components/ui/location-display-eon";
import ShareDropdown from "~/components/ui/share-dropdown-eon";
import { WatchJobButton } from "~/components/ui/watch-job-button-eon";
import { toSimilarJob } from "~/lib/utils/to-similar-job";
import { useLanguage } from "~/translations/loader/use-language";
import type { JobadComponentProps } from "~/types/job";
const JOB_VIDEOS: Record<string, {
  thumbnail: string;
  video: string;
}> = {
  "498": {
    video: "sverige/498.mp4",
    thumbnail: "images/videos/sverige/thumbnail-498.webp"
  },
  "499": {
    video: "sverige/499.mp4",
    thumbnail: "images/videos/sverige/thumbnail-499.webp"
  }
};

// Splits the task HTML right before the contact block (lookahead keeps the
// matched line) so it can be rendered separately in the contact section.
export const SPLIT_REGEX = /(?=(?:<(?:b|strong)>\s*)?(?:För frågor om tjänsten|For questions regarding the position|For questions about the position, please contact:|För fackliga frågor|For questions about the position, please contact the recruiting manager:))/;
export default function JobAdTemplateEonSverige(t0) {
  const $ = _c(128);
  const {
    job
  } = t0;
  const t = useLanguage();
  const {
    content,
    data
  } = job;
  let t1;
  if ($[0] !== content.compensationObject || $[1] !== t) {
    const salaryState = getSalaryState(content.compensationObject);
    t1 = formatSalaryRow(salaryState, t, t.i18n);
    $[0] = content.compensationObject;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const salaryRow = t1;
  const [newTask, setNewTask] = useState(content.offer);
  let t2;
  let t3;
  if ($[3] !== content.offer) {
    t2 = () => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content.offer, "text/html");
      const paragraphs = Array.from(doc.querySelectorAll("p"));
      const inclusionParagraph = paragraphs.find(_temp);
      const benefitParagraph = paragraphs.find(_temp2);
      if (inclusionParagraph && benefitParagraph) {
        inclusionParagraph.remove();
        benefitParagraph.remove();
      }
      setNewTask(doc.body.innerHTML.split(SPLIT_REGEX)[0]);
    };
    t3 = [content.offer];
    $[3] = content.offer;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== data.title) {
    t4 = <h2 className="mb-4 font-bold text-4xl text-primary md:text-5xl">{data.title}</h2>;
    $[6] = data.title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = !data.locations || data.locations.length === 1 ? `${t.jobad_locationDisplay_single}: ` : `${t.jobad_locationDisplay_multi}: `;
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="font-medium" data-source="lc">{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== job) {
    t7 = <span data-source="job"><LocationDisplay job={job} showAll={true} /></span>;
    $[10] = job;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div className="mb-10 text-lg">{t6}{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("overflow-wrap-break-word print:break-inside-auto print:text-black", "[&_p+p]:mt-4", "[&_p:not(li>p)]:mb-4 [&_p:not(li>p)]:print:my-0 [&_p:not(li>p)]:print:p-0", "[&_h1]:mb-4 [&_h1]:print:my-0 [&_h1]:print:p-0", "[&_h2]:mb-4 [&_h2]:print:my-0 [&_h2]:print:p-0", "[&_h3]:mb-4 [&_h3]:print:my-0 [&_h3]:print:p-0", "[&_li]:mb-1.5", "[&_ul]:mt-4 [&_ul]:mb-8 [&_ul]:ml-6 [&_ul]:list-disc [&_ul]:print:my-0 [&_ul]:print:py-0");
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t.jobad_description_HL) {
    t10 = <h3 className="sr-only">{t.jobad_description_HL}</h3>;
    $[16] = t.jobad_description_HL;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== content.offer || $[19] !== content.offerHL || $[20] !== newTask) {
    t11 = content.offer && <div className="mt-6">{content.offerHL && <h3 className="font-bold" data-source="job">{content.offerHL}</h3>}<div dangerouslySetInnerHTML={{
        __html: newTask
      }} data-source="job" /></div>;
    $[18] = content.offer;
    $[19] = content.offerHL;
    $[20] = newTask;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== content.profile || $[23] !== content.profileHL) {
    t12 = content.profile && <div className="mt-6">{content.profileHL && <h3 className="font-bold" data-source="job">{content.profileHL}</h3>}<div dangerouslySetInnerHTML={{
        __html: content.profile
      }} data-source="job" /></div>;
    $[22] = content.profile;
    $[23] = content.profileHL;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t12) {
    t13 = <div className={t9}>{t10}{t11}{t12}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== data.jobNumber) {
    t14 = JOB_VIDEOS[data.jobNumber] && <JobAdVideo className="bg-card" thumbnailSrc={JOB_VIDEOS[data.jobNumber].thumbnail} videoUrl={JOB_VIDEOS[data.jobNumber].video} />;
    $[29] = data.jobNumber;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t.jobad_details_HL) {
    t15 = <h3 className="mb-6 font-bold text-2xl" data-source="lc">{t.jobad_details_HL}:</h3>;
    $[31] = t.jobad_details_HL;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t.jobad_details_contractType) {
    t16 = <th data-source="lc">{t.jobad_details_contractType}:</th>;
    $[33] = t.jobad_details_contractType;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== data.contract) {
    t17 = <td data-source="job">{data.contract}</td>;
    $[35] = data.contract;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t16 || $[38] !== t17) {
    t18 = <tr>{t16}{t17}</tr>;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t.jobad_details_employmentType) {
    t19 = <th data-source="lc">{t.jobad_details_employmentType}:</th>;
    $[40] = t.jobad_details_employmentType;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== data.employmentType || $[43] !== t) {
    t20 = combineEmploymentTypes(data.employmentType, t);
    $[42] = data.employmentType;
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t20) {
    t21 = <td data-source="job">{t20}</td>;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t19 || $[48] !== t21) {
    t22 = <tr>{t19}{t21}</tr>;
    $[47] = t19;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t.jobad_details_remote) {
    t23 = <th data-source="lc">{t.jobad_details_remote}:</th>;
    $[50] = t.jobad_details_remote;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== data.remote) {
    t24 = <td data-source="job">{data.remote}</td>;
    $[52] = data.remote;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t23 || $[55] !== t24) {
    t25 = <tr>{t23}{t24}</tr>;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t.jobad_details_jobNumber) {
    t26 = <th data-source="lc">{t.jobad_details_jobNumber}:</th>;
    $[57] = t.jobad_details_jobNumber;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== data.jobNumber) {
    t27 = <td data-source="job">{data.jobNumber}</td>;
    $[59] = data.jobNumber;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t26 || $[62] !== t27) {
    t28 = <tr>{t26}{t27}</tr>;
    $[61] = t26;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== salaryRow) {
    t29 = salaryRow && <tr><th data-source="lc">{salaryRow.label}:</th><td data-source="job">{salaryRow.value}</td></tr>;
    $[64] = salaryRow;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== data.applicationEnd || $[67] !== t) {
    t30 = data.applicationEnd && !isBeforeToday(data.applicationEnd) && <tr><th data-source="lc">{t.jobad_details_applicationDeadlineTitle}:</th><td data-source="job">{displayDate(data.applicationEnd.slice(0, 10), t)}</td></tr>;
    $[66] = data.applicationEnd;
    $[67] = t;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t18 || $[70] !== t22 || $[71] !== t25 || $[72] !== t28 || $[73] !== t29 || $[74] !== t30) {
    t31 = <table className="wrap-break-word mb-8 w-full text-left md:w-2/3 lg:w-full lg:table-fixed"><tbody className="mb-4">{t18}{t22}{t25}{t28}{t29}{t30}</tbody></table>;
    $[69] = t18;
    $[70] = t22;
    $[71] = t25;
    $[72] = t28;
    $[73] = t29;
    $[74] = t30;
    $[75] = t31;
  } else {
    t31 = $[75];
  }
  let t32;
  if ($[76] !== salaryRow || $[77] !== t.salary_info_text_additional) {
    t32 = salaryRow && <p data-source="lc">{t.salary_info_text_additional}</p>;
    $[76] = salaryRow;
    $[77] = t.salary_info_text_additional;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== data.applicationEnd || $[80] !== t.sverige_applicationDeadline) {
    t33 = (!data.applicationEnd || isBeforeToday(data.applicationEnd)) && <p data-source="lc">{t.sverige_applicationDeadline}</p>;
    $[79] = data.applicationEnd;
    $[80] = t.sverige_applicationDeadline;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <PrivacyNotice className="mt-4" />;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  let t35;
  if ($[83] !== t15 || $[84] !== t31 || $[85] !== t32 || $[86] !== t33) {
    t35 = <section className="rounded bg-card p-10 leading-6 [&_td]:py-2 [&_td]:pl-2 [&_th]:py-2 [&_th]:align-text-top [&_th]:font-normal">{t15}{t31}{t32}{t33}{t34}</section>;
    $[83] = t15;
    $[84] = t31;
    $[85] = t32;
    $[86] = t33;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== data) {
    t36 = toSimilarJob(data);
    $[88] = data;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[90] !== t36) {
    t37 = <WatchJobButton className="h-full w-full" jobData={t36} variant="purple" />;
    $[90] = t36;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  let t38;
  if ($[92] !== job) {
    t38 = <ShareDropdown className="h-full w-full" job={job} variant="purple" />;
    $[92] = job;
    $[93] = t38;
  } else {
    t38 = $[93];
  }
  let t39;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Printer className="size-6" />;
    $[94] = t39;
  } else {
    t39 = $[94];
  }
  let t40;
  if ($[95] !== t.jobad_print) {
    t40 = <Button aria-label={t.jobad_print} className="h-full w-full" onClick={_temp3} variant="purple">{t39}</Button>;
    $[95] = t.jobad_print;
    $[96] = t40;
  } else {
    t40 = $[96];
  }
  let t41;
  if ($[97] !== t37 || $[98] !== t38 || $[99] !== t40) {
    t41 = <section className="grid h-10 grid-flow-col grid-cols-3 gap-4 text-white">{t37}{t38}{t40}</section>;
    $[97] = t37;
    $[98] = t38;
    $[99] = t40;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] !== t.jobad_inclusionHL) {
    t42 = <p className="font-bold" data-source="lc">{t.jobad_inclusionHL}</p>;
    $[101] = t.jobad_inclusionHL;
    $[102] = t42;
  } else {
    t42 = $[102];
  }
  let t43;
  if ($[103] !== t.sverige_inclusion_text) {
    t43 = <p data-source="lc">{t.sverige_inclusion_text}</p>;
    $[103] = t.sverige_inclusion_text;
    $[104] = t43;
  } else {
    t43 = $[104];
  }
  let t44;
  if ($[105] !== t42 || $[106] !== t43) {
    t44 = <article className="rounded bg-card p-10">{t42}{t43}</article>;
    $[105] = t42;
    $[106] = t43;
    $[107] = t44;
  } else {
    t44 = $[107];
  }
  let t45;
  if ($[108] !== t.sverige_teaser_benefits) {
    t45 = <p className="font-bold" data-source="lc">{t.sverige_teaser_benefits}</p>;
    $[108] = t.sverige_teaser_benefits;
    $[109] = t45;
  } else {
    t45 = $[109];
  }
  let t46;
  if ($[110] !== t.sverige_benefits_text) {
    t46 = <p data-source="lc">{t.sverige_benefits_text}</p>;
    $[110] = t.sverige_benefits_text;
    $[111] = t46;
  } else {
    t46 = $[111];
  }
  let t47;
  if ($[112] !== t45 || $[113] !== t46) {
    t47 = <article className="rounded bg-primary p-10 text-white">{t45}{t46}</article>;
    $[112] = t45;
    $[113] = t46;
    $[114] = t47;
  } else {
    t47 = $[114];
  }
  let t48;
  if ($[115] !== t14 || $[116] !== t35 || $[117] !== t41 || $[118] !== t44 || $[119] !== t47) {
    t48 = <div className="flex flex-col gap-4">{t14}{t35}{t41}{t44}{t47}</div>;
    $[115] = t14;
    $[116] = t35;
    $[117] = t41;
    $[118] = t44;
    $[119] = t47;
    $[120] = t48;
  } else {
    t48 = $[120];
  }
  let t49;
  if ($[121] !== t13 || $[122] !== t48) {
    t49 = <div className="grid-cols-[minmax(0,1fr)_400px] gap-x-24 text-xl md:text-lg lg:grid xl:gap-x-32">{t13}{t48}</div>;
    $[121] = t13;
    $[122] = t48;
    $[123] = t49;
  } else {
    t49 = $[123];
  }
  let t50;
  if ($[124] !== t4 || $[125] !== t49 || $[126] !== t8) {
    t50 = <section className="outer-container py-container" id="job"><div className="inner-container">{t4}{t8}{t49}</div></section>;
    $[124] = t4;
    $[125] = t49;
    $[126] = t8;
    $[127] = t50;
  } else {
    t50 = $[127];
  }
  return t50;
}
function _temp3() {
  return window.print();
}
function _temp2(p_0) {
  return p_0.textContent?.includes("Vi arbetar f\xF6r ett mer");
}
function _temp(p) {
  return p.textContent?.includes("P\xE5 E.ON arbetar vi aktivt med inkludering");
}